/**
 * Weekly launch-plan generator modal with CSV/Markdown export.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowsClockwise, Rocket, FileCsv, FileText, X, DotsSixVertical } from "@phosphor-icons/react";

export function LaunchPlanModal({ posts, products, onClose }) {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(
    () => posts.filter((p) => filter === "all" || p.estado === filter),
    [posts, filter],
  );
  const autoPlan = useMemo(() => buildPlan(filtered), [filtered]);

  // Custom order: array of post ids; null = use IA order
  const [customOrder, setCustomOrder] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Reset custom order when the filter or post list changes. Deferred via
  // queueMicrotask to satisfy the React 19 set-state-in-effect rule.
  useEffect(() => {
    queueMicrotask(() => setCustomOrder(null));
  }, [filter, posts]);

  const plan = useMemo(() => {
    if (!customOrder) return autoPlan;
    const byId = Object.fromEntries(autoPlan.map((p) => [p.id, p]));
    const ordered = customOrder.map((id) => byId[id]).filter(Boolean);
    // include any new post not in custom order (e.g. just generated) at the end
    autoPlan.forEach((p) => {
      if (!customOrder.includes(p.id)) ordered.push(p);
    });
    return applySchedule(ordered);
  }, [autoPlan, customOrder]);

  const isCustom = !!customOrder;

  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", id);
    } catch {
      // Some older browsers throw on dataTransfer mutations outside of
      // dragstart. Drag still works because we track the id in state.
    }
  };
  const onDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== dragOverId) setDragOverId(id);
  };
  const onDrop = (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) {
      setDragId(null);
      setDragOverId(null);
      return;
    }
    const order = (customOrder || plan.map((p) => p.id)).slice();
    const from = order.indexOf(dragId);
    const to = order.indexOf(targetId);
    if (from === -1 || to === -1) return;
    order.splice(from, 1);
    order.splice(to, 0, dragId);
    setCustomOrder(order);
    setDragId(null);
    setDragOverId(null);
  };
  const onDragEnd = () => {
    setDragId(null);
    setDragOverId(null);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="launch-plan-modal"
    >
      <div
        className="bg-white border border-zinc-200 max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-zinc-200 gap-4">
          <div>
            <div className="overline text-klein flex items-center gap-1.5">
              <Rocket size={12} weight="fill" /> Plan de lanzamiento
            </div>
            <h2 className="h-display text-3xl font-bold tracking-tight mt-1">
              {isCustom ? "Orden personalizado" : "Ordenado por predicción"}
            </h2>
            <p className="text-xs text-muted2 mt-1">
              Arrastra cualquier fila para reordenar. Los horarios se recalculan automáticamente.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100" data-testid="close-plan">
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2 text-xs">
            <span className="overline">Filtrar</span>
            <select
              className="input-flat py-1 text-xs"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              data-testid="plan-filter"
            >
              <option value="all">Todas</option>
              <option value="borrador">Borradores</option>
              <option value="publicado">Publicadas</option>
              <option value="mejorado">Mejoradas</option>
            </select>
            <span className="text-muted2 ml-2">{plan.length} publicaciones</span>
            {isCustom && (
              <span className="ml-2 px-2 py-0.5 bg-klein text-white text-[10px] uppercase tracking-[0.2em]">
                Orden manual
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {isCustom && (
              <button
                className="btn-ghost"
                onClick={() => setCustomOrder(null)}
                data-testid="btn-reset-order"
              >
                <ArrowsClockwise size={16} /> Restablecer
              </button>
            )}
            <button
              className="btn-ghost"
              onClick={() => exportCsv(plan, products)}
              disabled={plan.length === 0}
              data-testid="btn-export-csv"
            >
              <FileCsv size={16} /> CSV
            </button>
            <button
              className="btn-ghost"
              onClick={() => exportMd(plan, products)}
              disabled={plan.length === 0}
              data-testid="btn-export-md"
            >
              <FileText size={16} /> Markdown
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {plan.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted2">No hay publicaciones que mostrar.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white sticky top-0 border-b border-zinc-200">
                <tr className="text-[11px] uppercase tracking-[0.2em] font-medium text-muted2">
                  <th className="text-left px-2 py-3 w-8"></th>
                  <th className="text-left px-4 py-3 w-12">#</th>
                  <th className="text-left px-4 py-3">Cuándo</th>
                  <th className="text-left px-4 py-3">Plataforma</th>
                  <th className="text-left px-4 py-3">Publicación</th>
                  <th className="text-right px-4 py-3 w-32">Predicción</th>
                  <th className="text-right px-4 py-3 w-24">Real</th>
                </tr>
              </thead>
              <tbody>
                {plan.map((p) => {
                  const mid = ((p.prediccion?.score_min || 0) + (p.prediccion?.score_max || 0)) / 2;
                  const color = mid >= 60 ? "#00C853" : mid >= 30 ? "#FFCC00" : "#FF2A00";
                  const isDragging = dragId === p.id;
                  const isOver = dragOverId === p.id && dragId && dragId !== p.id;
                  return (
                    <tr
                      key={p.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, p.id)}
                      onDragOver={(e) => onDragOver(e, p.id)}
                      onDrop={(e) => onDrop(e, p.id)}
                      onDragEnd={onDragEnd}
                      className={`border-b border-zinc-100 cursor-move transition-colors ${
                        isDragging ? "opacity-40" : ""
                      } ${isOver ? "bg-klein/5 border-t-2 border-t-klein" : "hover:bg-zinc-50"}`}
                      data-testid={`plan-row-${p.id}`}
                    >
                      <td className="px-2 py-3 text-zinc-300 select-none" title="Arrastrar para reordenar">
                        <DotsSixVertical size={16} />
                      </td>
                      <td className="px-4 py-3 font-mono tabular-nums text-muted2">{p._rank}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium">
                          {p._scheduled.toLocaleDateString("es-MX", {
                            weekday: "short",
                            day: "2-digit",
                            month: "short",
                          })}
                        </div>
                        <div className="text-xs text-muted2 font-mono">{p._slot}</div>
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <span className="px-2 py-0.5 bg-klein text-white">
                          {PLATFORM_LABELS[p.plataforma] || p.plataforma}
                        </span>
                      </td>
                      <td className="px-4 py-3 min-w-0">
                        <div className="font-medium truncate max-w-md">{p.titulo}</div>
                        <div className="text-xs text-muted2 truncate max-w-md">
                          {products[p.product_id]?.nombre || "—"} · v{p.version} · {p.estado}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums" style={{ color }}>
                        {p.prediccion?.score_max ? `${p.prediccion.score_min}–${p.prediccion.score_max}` : "—"}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-medium">
                        {p.score > 0 ? p.score : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


const SLOTS = {
  // optimal posting slots in 24h based on industry benchmarks
  instagram: ["11:00", "14:00", "19:00"],
  facebook: ["09:00", "13:00", "15:00"],
  twitter: ["08:00", "12:00", "17:00"],
  tiktok: ["19:00", "21:00", "23:00"],
  whatsapp: ["09:30", "13:30", "19:30"],
};


const PLATFORM_LABELS = {
  instagram: "Instagram",
  facebook: "Facebook",
  twitter: "Twitter / X",
  tiktok: "TikTok",
  whatsapp: "WhatsApp",
};


function midScore(p) {
  const pred = p.prediccion || { score_min: 0, score_max: 0 };
  return ((Number(pred.score_min) || 0) + (Number(pred.score_max) || 0)) / 2;
}


function applySchedule(orderedPosts) {
  // Assign rank + slot to a pre-ordered list
  const counters = {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return orderedPosts.map((p, i) => {
    const platform = p.plataforma in SLOTS ? p.plataforma : "instagram";
    const slots = SLOTS[platform];
    const c = (counters[platform] = counters[platform] || 0);
    counters[platform] = c + 1;
    const dayOffset = Math.floor(c / slots.length) + 1;
    const slotIdx = c % slots.length;
    const date = new Date(today);
    date.setDate(date.getDate() + dayOffset);
    const [hh, mm] = slots[slotIdx].split(":").map(Number);
    date.setHours(hh, mm, 0, 0);
    return { ...p, _rank: i + 1, _scheduled: date, _slot: slots[slotIdx] };
  });
}


function buildPlan(posts) {
  const ranked = [...posts]
    .filter((p) => p.estado !== "archivado")
    .sort((a, b) => midScore(b) - midScore(a) || (b.score || 0) - (a.score || 0));
  return applySchedule(ranked);
}


function downloadFile(filename, content, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}


function escapeCsv(v) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}


function exportCsv(plan, productMap) {
  const headers = [
    "rank",
    "fecha",
    "hora",
    "plataforma",
    "producto",
    "titulo",
    "score_min",
    "score_max",
    "score_real",
    "estado",
    "version",
    "cta",
    "enlace",
    "hashtags",
    "contenido",
  ];
  const rows = plan.map((p) => [
    p._rank,
    p._scheduled.toISOString().slice(0, 10),
    p._slot,
    PLATFORM_LABELS[p.plataforma] || p.plataforma,
    productMap[p.product_id]?.nombre || "",
    p.titulo,
    p.prediccion?.score_min ?? "",
    p.prediccion?.score_max ?? "",
    p.score || 0,
    p.estado,
    p.version,
    p.cta || "",
    p.enlace_producto || "",
    (p.hashtags || []).map((h) => `#${h}`).join(" "),
    (p.contenido || "").replace(/\s+/g, " ").trim(),
  ]);
  const csv = [headers, ...rows].map((r) => r.map(escapeCsv).join(",")).join("\n");
  downloadFile(`plan-vendeia-${new Date().toISOString().slice(0, 10)}.csv`, csv, "text/csv;charset=utf-8");
}


function exportMd(plan, productMap) {
  const lines = [
    `# Plan de lanzamiento — VendeIA`,
    ``,
    `Generado: ${new Date().toLocaleString("es-MX")}`,
    `Total de publicaciones: ${plan.length}`,
    ``,
  ];
  for (const p of plan) {
    const fecha = p._scheduled.toLocaleDateString("es-MX", { weekday: "long", day: "2-digit", month: "long" });
    lines.push(`## #${p._rank} · ${PLATFORM_LABELS[p.plataforma] || p.plataforma} — ${fecha} ${p._slot}`);
    lines.push(``);
    lines.push(`**Producto:** ${productMap[p.product_id]?.nombre || "—"}  `);
    lines.push(`**Título:** ${p.titulo}  `);
    if (p.prediccion?.score_max) {
      lines.push(`**Predicción IA:** ${p.prediccion.score_min}–${p.prediccion.score_max} / 100`);
      if (p.prediccion.razonamiento) lines.push(`> ${p.prediccion.razonamiento}`);
    }
    lines.push(``);
    lines.push(p.contenido || "");
    if (p.cta) lines.push(``, `**CTA:** ${p.cta}`);
    if (p.enlace_producto) lines.push(``, `**Enlace:** ${p.enlace_producto}`);
    if (p.hashtags?.length) lines.push(``, p.hashtags.map((h) => `#${h}`).join(" "));
    lines.push(``, `---`, ``);
  }
  downloadFile(`plan-vendeia-${new Date().toISOString().slice(0, 10)}.md`, lines.join("\n"), "text/markdown;charset=utf-8");
}
